<template>
  <RevButton
    :disabled
    full-width="always"
    :loading
    variant="secondary"
    @click.stop.prevent="addToCartHandler(id)"
  >
    <span class="body-1-bold">{{ text }}</span>
  </RevButton>
</template>

<script lang="ts" setup>
import { navigateTo, useRoute } from '#imports'
import { computed, onMounted, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'

import { useAddToCart } from '../../composables/useAddToCart'
import {
  type ProductTrackingData,
  useAddToCartTracking,
} from '../../composables/useProductTracking'

import translations from './AddToCartButton.translations'

const { trackingData, productTitle } = defineProps<{
  text: string
  id: string
  trackingData: ProductTrackingData
  productTitle: string
}>()

const disabled = ref(true)
onMounted(() => {
  disabled.value = false
})

const emit = defineEmits(['add-to-cart'])

const { addToCart, addingToCart } = useAddToCart()
const { openSuccessToast, openErrorToast } = useTheToast()
const i18n = useI18n()
const locale = useI18nLocale()
const route = useRoute()
const loading = computed(() => addingToCart.value === 'pending')

const isCheckoutRoute = computed(() => {
  return route.name?.toString().startsWith('checkout')
})

async function addToCartHandler(id: string) {
  await addToCart(id)
  if (addingToCart.value === 'success') {
    const { trackAddToCart } = useAddToCartTracking()
    trackAddToCart(trackingData)

    openSuccessToast({
      title: i18n(translations.toastSuccessTitle),
      content: productTitle,
      ...(!isCheckoutRoute.value && {
        actionLabel: i18n(translations.toastSucessCta),
        actionCallback: goToCart,
      }),
    })
  } else if (addingToCart.value === 'error') {
    openErrorToast({
      title: i18n(translations.toastErrorTitle),
      content: i18n(translations.toastErrorMessage),
    })
  }
  emit('add-to-cart', { status: addingToCart.value })
}

async function goToCart() {
  await navigateTo({
    name: 'checkout_cart',
    params: { locale },
  })
}
</script>
